
import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ChevronDown, ChevronUp } from "lucide-react";
import { ProjectTask } from "../types";
import { Checkbox } from "@/components/ui/checkbox";
import { calculateEndDate } from "../utils/dateUtils";

interface TemplateDialogProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  selectedTemplate: string;
  templateTasks: ProjectTask[];
  onTaskDurationChange: (index: number, increase: boolean) => void;
  startDate: string;
  endDate: string;
  projectName: string;
  projectDescription: string;
  projectLead: string;
  onApplyTemplate: (e: React.FormEvent) => void;
  onStartDateChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onProjectNameChange: (value: string) => void;
  onProjectDescriptionChange: (value: string) => void;
  onProjectLeadChange: (value: string) => void;
  excludeWeekends: boolean;
  onExcludeWeekendsChange: (checked: boolean) => void;
  existingProjects?: { project_name: string }[];
}

export const TemplateDialog = ({
  isOpen,
  onOpenChange,
  selectedTemplate,
  templateTasks,
  onTaskDurationChange,
  startDate,
  endDate,
  projectName,
  projectDescription,
  projectLead,
  onApplyTemplate,
  onStartDateChange,
  onProjectNameChange,
  onProjectDescriptionChange,
  onProjectLeadChange,
  excludeWeekends,
  onExcludeWeekendsChange,
  existingProjects = []
}: TemplateDialogProps) => {
  const [calculatedEndDate, setCalculatedEndDate] = useState(endDate);
  const [isDuplicateName, setIsDuplicateName] = useState(false);

  // Check for duplicate project name (case-insensitive)
  useEffect(() => {
    if (projectName.trim()) {
      const normalizedName = projectName.trim().toLowerCase();
      const isDuplicate = existingProjects.some(
        project => project.project_name.toLowerCase() === normalizedName
      );
      setIsDuplicateName(isDuplicate);
    } else {
      setIsDuplicateName(false);
    }
  }, [projectName, existingProjects]);

  // Auto-calculate end date when start date, template tasks, or excludeWeekends changes
  useEffect(() => {
    if (startDate && templateTasks.length > 0) {
      const totalDuration = templateTasks.reduce((sum, task) => sum + task.duration, 0);
      const newEndDate = calculateEndDate(startDate, totalDuration, excludeWeekends);
      setCalculatedEndDate(newEndDate);
    } else {
      setCalculatedEndDate("");
    }
  }, [startDate, templateTasks, excludeWeekends]);

  // Format the calculated end date to match the expected input format
  const formatDateForInput = (dateStr: string): string => {
    if (!dateStr) return "";
    try {
      // Parse the date string (format: "dd MMM yyyy")
      const parts = dateStr.split(' ');
      if (parts.length === 3) {
        const day = parts[0].padStart(2, '0');
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                           'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const month = (monthNames.indexOf(parts[1]) + 1).toString().padStart(2, '0');
        const year = parts[2];
        return `${year}-${month}-${day}`;
      }
      return dateStr;
    } catch (error) {
      console.error('Error formatting date:', error);
      return dateStr;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isDuplicateName) {
      return;
    }
    
    await onApplyTemplate(e);
    // Dialog will be closed by the parent component after successful project creation
  };

  return <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[105vh] bg-white text-black overflow-y-auto">
        <DialogHeader className="py-0">
          <DialogTitle>Use {selectedTemplate} Template</DialogTitle>
          <DialogDescription className="py-0 h-[15px] text-red-500">
            Customize project details before applying the template to the new project, e.g. change the duration of project tasks.
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit}>
          <div className="flex flex-row gap-6">
            {/* Left column - Project details */}
            <div className="w-1/2 space-y-4 py-4">
              <div className="grid grid-cols-4 items-center gap-4 py-2">
                <Label htmlFor="templateProjectName" className="text-black text-left font-bold">
                  Project Name
                </Label>
                <div className="col-span-3">
                  <Input 
                    id="templateProjectName" 
                    value={projectName} 
                    onChange={e => onProjectNameChange(e.target.value)} 
                    className="w-full bg-white text-black" 
                    placeholder="Enter project name" 
                  />
                  {isDuplicateName && (
                    <p className="text-red-500 text-sm mt-1">
                      This project name already exists. Please enter a unique name.
                    </p>
                  )}
                </div>
              </div>
              
              <div className="grid grid-cols-4 items-center gap-4 py-2">
                <Label htmlFor="templateProjectDescription" className="text-black text-left font-bold">
                  Description
                </Label>
                <Textarea id="templateProjectDescription" value={projectDescription} onChange={e => onProjectDescriptionChange(e.target.value)} className="col-span-3 bg-white text-black" placeholder="Enter project description" />
              </div>
              
              <div className="grid grid-cols-4 items-center gap-4 py-2">
                <Label htmlFor="templateProjectLead" className="text-black text-left font-bold">
                  Project Lead's Email
                </Label>
                <Input id="templateProjectLead" value={projectLead} onChange={e => onProjectLeadChange(e.target.value)} className="col-span-3 bg-white text-black" placeholder="Enter project lead's email" />
              </div>
              
              {/* Dates */}
              <div className="flex space-x-0 pt-0 text-left">
                <div className="flex-1 py-1">
                  <Label htmlFor="templateStartDate" className="text-black block mb-4 font-bold">
                    Project Start Date
                  </Label>
                  <Input id="templateStartDate" type="date" value={startDate} onChange={onStartDateChange} className="w-full bg-white text-black" />
                </div>
                
                <div className="flex-1 py-1">
                  <Label htmlFor="templateEndDate" className="text-black block mb-4 font-bold">Project End Date</Label>
                  <Input 
                    id="templateEndDate" 
                    type="date" 
                    value={formatDateForInput(calculatedEndDate)} 
                    readOnly 
                    disabled 
                    className="w-full bg-white text-black" 
                  />
                </div>
                
                <div className="flex-1 py-1">
                  <Label htmlFor="totalDuration" className="text-black block mb-4 font-bold">
                    Total Duration
                  </Label>
                  <Input id="totalDuration" type="text" value={`${templateTasks.reduce((sum, task) => sum + task.duration, 0)} days`} readOnly disabled className="w-full bg-white text-black" />
                </div>
              </div>

              {/* Weekend Exclusion Checkbox */}
              <div className="flex items-center space-x-2 py-4">
                <Checkbox 
                  id="excludeWeekends" 
                  checked={excludeWeekends} 
                  onCheckedChange={onExcludeWeekendsChange}
                />
                <Label htmlFor="excludeWeekends" className="text-sm font-medium">
                  Exclude weekends when projecting date and duration
                </Label>
              </div>
            </div>
            
            {/* Right column - Tasks */}
            <div className="w-1/2 py-1">
              <div className="flex justify-between items-center mb-1">
                <h3 className="font-bold">Project Tasks</h3>
                <h3 className="font-bold text-right">Estimated Days</h3>
              </div>
              <div className="border rounded-md divide-y max-h-[350px] overflow-y-auto">
                {templateTasks.map((task, index) => <div key={index} className="flex items-center justify-between p-0 hover:bg-slate-100 hover:text-black">
                    <span className="flex-grow text-[14px] mx-[10px]">{task.name}</span>
                    <div className="flex items-center">
                      <Button type="button" variant="ghost" size="sm" onClick={() => onTaskDurationChange(index, false)} className="h-8 w-8 p-0 text-violet-600">
                        <ChevronDown className="h-4 w-4" />
                      </Button>
                      <span className="w-10 text-center text-[14px]">{task.duration}</span>
                      <Button type="button" variant="ghost" size="sm" onClick={() => onTaskDurationChange(index, true)} className="h-8 w-8 p-0 text-purple-600">
                        <ChevronUp className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>)}
              </div>
            </div>
          </div>
          
          <DialogFooter className="mt-2">
            <div className="flex w-[350px] justify-between px-3">
              <Button className="bg-white text-black" type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={isDuplicateName || !projectName.trim()}
              >
                Apply Template To New Project
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>;
};
